import { createRouter } from "@tanstack/solid-router";
import { NotFoundRoute } from "@/components/not-found-route";
import { rootRoute } from "@/routes/__root";
import { authRoute } from "@/routes/_auth/route";
import { loginRoute } from "@/routes/_auth/login";
import { registerRoute } from "@/routes/_auth/register";
import { forgotPasswordRoute } from "@/routes/_auth/forgot-password";
import { resetPasswordRoute } from "@/routes/_auth/reset-password";
import { verifyEmailRoute } from "@/routes/_auth/verify-email";
import { socialCallbackRoute } from "@/routes/_auth/social-callback";
import { appRoute } from "@/routes/_app/route";
import { indexRoute } from "@/routes/_app/index";
import { profileRoute } from "@/routes/_app/profile";
import { debugRoute } from "@/routes/_app/debug";
import { organizationsRoute } from "@/routes/_app/organizations";
import { organizationDetailRoute } from "@/routes/_app/organizations.$orgId";
import { invitationsRoute } from "@/routes/_app/invitations";
import { adminRoute } from "@/routes/_app/admin";
import { adminIndexRoute } from "@/routes/_app/admin.index";
import { adminUsersRoute } from "@/routes/_app/admin.users";
import { adminAuditLogRoute } from "@/routes/_app/admin.audit-log";
import { adminIntegrationsRoute } from "@/routes/_app/admin.integrations";
import { adminFlagsRoute } from "@/routes/_app/admin.flags";
import { adminMetricsRoute } from "@/routes/_app/admin.metrics";
import { adminStorageRoute } from "@/routes/_app/admin.storage";
import { adminThemeAuditRoute } from "@/routes/_app/admin.theme-audit";
import { adminThemesRoute } from "@/routes/_app/admin.themes";
import { adminFontsRoute } from "@/routes/_app/admin.fonts";
import { adminEmailRoute } from "@/routes/_app/admin.email";
import { adminEmailCampaignsRoute } from "@/routes/_app/admin.email.campaigns";
import { themeGalleryRoute } from "@/routes/_app/themes";
import { languageRoute } from "@/routes/_app/language";
import { profileFontsRoute } from "@/routes/_app/profile.fonts";
import { profileShortcutsRoute } from "@/routes/_app/profile.shortcuts";
import { profileDeleteAccountRoute } from "@/routes/_app/profile.delete-account";
import { profileTransferSendRoute } from "@/routes/_app/profile.transfer-send";
import { profileTransferReceiveRoute } from "@/routes/_app/profile.transfer-receive";
import { productsRoute } from "@/routes/_app/products";
import { warehousesRoute } from "@/routes/_app/warehouses";
import { cc21ProbeRoute } from "@/routes/_app/cc21-probe";
import { cc26ProbeRoute } from "@/routes/_app/cc26-probe";
import { stockRoute } from "@/routes/_app/stock";
import { operationsRoute } from "@/routes/_app/operations";
import { replenishmentRoute } from "@/routes/_app/replenishment";
import { purchasesRoute } from "@/routes/_app/purchases";
import { valuationRoute } from "@/routes/_app/valuation";
import { lotsRoute } from "@/routes/_app/lots";
import { countsRoute } from "@/routes/_app/counts";
import { manufacturingRoute } from "@/routes/_app/manufacturing";
import { activityRoute } from "@/routes/_app/activity";
import { achievementsRoute } from "@/routes/_app/achievements";
import { trashRoute } from "@/routes/_app/trash";

const routeTree = rootRoute.addChildren([
  socialCallbackRoute,
  authRoute.addChildren([
    loginRoute,
    registerRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyEmailRoute,
  ]),
  appRoute.addChildren([
    // Fixtures de regresión CC-21 (Select/Popover alcanzable dentro de un Dialog
    // modal) y CC-26 (Select respaldado por query LIVE: el click en la opción no
    // se pierde por remount). Solo en DEV: sirven a cc21-probe.spec /
    // select-click.spec y NO se registran en prod.
    ...(import.meta.env.DEV ? [cc21ProbeRoute, cc26ProbeRoute] : []),
    indexRoute, // path: "/" — Dashboard
    productsRoute, // path: "/products" — Catálogo (Stockpilot F1)
    warehousesRoute, // path: "/warehouses" — Almacenes + ubicaciones
    stockRoute, // path: "/stock" — Existencias + ajuste de inventario
    operationsRoute, // path: "/operations" — Recepción/entrega/transferencia (F2)
    replenishmentRoute, // path: "/replenishment" — Reglas + sugerencias (F2)
    purchasesRoute, // path: "/purchases" — Proveedores + órdenes de compra (F3)
    valuationRoute, // path: "/valuation" — Valor de inventario AVCO (F3)
    lotsRoute, // path: "/lots" — Lotes/series + caducidad (F4)
    countsRoute, // path: "/counts" — Conteo cíclico / inventario físico (F4)
    manufacturingRoute, // path: "/manufacturing" — BoM + órdenes + landed costs + devoluciones (F5)
    activityRoute, // path: "/activity" — Feed "quién hizo qué" (ACTIVITY-CORE L3)
    achievementsRoute, // path: "/achievements" — XP/racha/badges (GAMIFICATION-CORE)
    trashRoute, // path: "/trash" — Papelera restaurable (ACTIVITY-CORE L2)
    organizationsRoute,
    organizationDetailRoute,
    invitationsRoute,
    adminRoute.addChildren([
      adminIndexRoute,
      adminUsersRoute,
      adminAuditLogRoute,
      adminIntegrationsRoute,
      adminFlagsRoute,
      adminMetricsRoute,
      adminStorageRoute,
      adminThemeAuditRoute,
      adminThemesRoute,
      adminFontsRoute,
      adminEmailRoute,
      adminEmailCampaignsRoute,
    ]),
    themeGalleryRoute,
    languageRoute,
    profileRoute,
    profileFontsRoute,
    profileShortcutsRoute,
    profileDeleteAccountRoute,
    profileTransferSendRoute,
    profileTransferReceiveRoute,
    debugRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  // Cualquier URL desconocida muestra un estado "no encontrado" con acción para
  // volver al inicio, en lugar de una pantalla en blanco.
  defaultNotFoundComponent: NotFoundRoute,
  // "intent" preloads only when the user expresses intent (hover/focus on a
  // <Link>). The previous "viewport" setting eagerly fired `beforeLoad` and
  // PowerSync `resolveTables` EXPLAIN queries for ~20 sidebar routes the
  // moment the dashboard mounted. Combined with PowerSync's initial-sync
  // batches (each one re-invalidating subscribed queries), this saturated
  // the main thread for several seconds after login. Hover-only is the
  // standard tradeoff: a tiny first-hover latency in exchange for a calm
  // post-login render.
  defaultPreload: "intent",
  // oxlint-disable-next-line eslint(no-magic-numbers)
  defaultPreloadStaleTime: 30_000, // 30 s — don't re-preload the same route within 30 s
});

declare module "@tanstack/solid-router" {
  interface Register {
    router: typeof router;
  }
}
